/**
 * Recording blob store (ACA-79) — durable, cross-reload persistence.
 *
 * Backs {@link recordingBlobCache} with IndexedDB so a consult's recorded audio
 * survives a full page reload/reopen, letting a failed upload be resumed on the
 * next app load instead of being lost. Blobs are stored natively by IndexedDB
 * (no base64), keyed by consultId.
 *
 * All operations are best-effort and guarded: if IndexedDB is unavailable
 * (e.g. jsdom, private-mode restrictions, SSR) every call resolves to a safe
 * no-op/empty result rather than throwing.
 */
import { logger } from 'app/logging/Logger'

export interface PendingRecording {
    consultId: string
    audioId: string | null
    blob: Blob
    createdAt: number
}

const DB_NAME = 'tuhi-uploads'
const DB_VERSION = 1
const STORE = 'pendingRecordings'

function hasIndexedDB(): boolean {
    return (
        typeof indexedDB !== 'undefined' &&
        indexedDB !== null &&
        typeof indexedDB.open === 'function'
    )
}

function openDb(): Promise<IDBDatabase | null> {
    if (!hasIndexedDB()) return Promise.resolve(null)

    return new Promise((resolve) => {
        try {
            const request = indexedDB.open(DB_NAME, DB_VERSION)
            request.onupgradeneeded = () => {
                const db = request.result
                if (!db.objectStoreNames.contains(STORE)) {
                    db.createObjectStore(STORE, { keyPath: 'consultId' })
                }
            }
            request.onsuccess = () => resolve(request.result)
            request.onerror = () => {
                logger.warn('recordingBlobStore: failed to open IndexedDB', {
                    action: 'recording_store_open_failed',
                })
                resolve(null)
            }
        } catch {
            resolve(null)
        }
    })
}

function tx<T>(
    db: IDBDatabase,
    mode: IDBTransactionMode,
    run: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T | undefined> {
    return new Promise((resolve) => {
        try {
            const transaction = db.transaction(STORE, mode)
            const request = run(transaction.objectStore(STORE))
            request.onsuccess = () => resolve(request.result as T)
            request.onerror = () => resolve(undefined)
        } catch {
            resolve(undefined)
        }
    })
}

/** Persist (or overwrite) a pending recording. Best-effort. */
export async function putRecording(record: PendingRecording): Promise<void> {
    const db = await openDb()
    if (!db) return
    await tx(db, 'readwrite', (store) => store.put(record))
    db.close()
}

/** Return every persisted pending recording. Empty if none / unavailable. */
export async function getAllRecordings(): Promise<PendingRecording[]> {
    const db = await openDb()
    if (!db) return []
    const result = await tx<PendingRecording[]>(db, 'readonly', (store) =>
        store.getAll()
    )
    db.close()
    return result ?? []
}

/** Delete a persisted recording by consultId. Best-effort. */
export async function deleteRecording(consultId: string): Promise<void> {
    const db = await openDb()
    if (!db) return
    await tx(db, 'readwrite', (store) => store.delete(consultId))
    db.close()
}
